// Engraved-banknote style hero for the Solar Portfolio section.
// Matches SoriginLogoBanner exactly in proportions, palette, frame, hatch,
// flourishes, sparkles and turbine treatment. The focal element is a tilted
// engraved solar panel array with an animated engraved sun above; a small
// decorative engraved wind turbine sits in the right corner.
import '../../../../components/branding/SoriginLogoBanner.css';
import './EngravedBanners.css';

export interface SolarFieldHeroProps {
  className?: string;
}

interface EngravedPanelProps {
  x: number;
  y: number;
  width: number;
  height: number;
  tilt: number; // degrees
  cols: number;
  rows: number;
}

function EngravedPanel({ x, y, width, height, tilt, cols, rows }: EngravedPanelProps) {
  // Draw panel centered at (x,y) then rotate by tilt around that point.
  const halfW = width / 2;
  const halfH = height / 2;
  const cellW = width / cols;
  const cellH = height / rows;
  return (
    <g transform={`translate(${x} ${y}) rotate(${tilt})`}>
      {/* Panel rim */}
      <rect
        x={-halfW}
        y={-halfH}
        width={width}
        height={height}
        rx={0.6}
        fill="#FBFEF9"
        fillOpacity={0.35}
        stroke="#076333"
        strokeOpacity={0.6}
        strokeWidth={0.55}
      />
      {/* Inner cell hatching (vertical) */}
      <g stroke="#006346" strokeOpacity={0.55} strokeWidth={0.35}>
        {Array.from({ length: cols - 1 }).map((_, i) => {
          const cx = -halfW + (i + 1) * cellW;
          return <line key={`v-${i}`} x1={cx} y1={-halfH + 0.6} x2={cx} y2={halfH - 0.6} />;
        })}
        {Array.from({ length: rows - 1 }).map((_, i) => {
          const cy = -halfH + (i + 1) * cellH;
          return <line key={`h-${i}`} x1={-halfW + 0.6} y1={cy} x2={halfW - 0.6} y2={cy} />;
        })}
      </g>
      {/* Cell diagonal engraving — adds the banknote shimmer */}
      <g stroke="#006346" strokeOpacity={0.18} strokeWidth={0.3}>
        {Array.from({ length: rows }).map((_, r) =>
          Array.from({ length: cols }).map((__, c) => {
            const cx = -halfW + c * cellW;
            const cy = -halfH + r * cellH;
            return (
              <line
                key={`d-${r}-${c}`}
                x1={cx + 0.6}
                y1={cy + cellH - 0.6}
                x2={cx + cellW - 0.6}
                y2={cy + 0.6}
              />
            );
          })
        )}
      </g>
      {/* Mint highlight strip top-left for engraved sheen */}
      <line
        x1={-halfW + 0.8}
        y1={-halfH + 0.8}
        x2={halfW - 0.8}
        y2={-halfH + 0.8}
        stroke="#52BF9D"
        strokeOpacity={0.55}
        strokeWidth={0.35}
      />
    </g>
  );
}

export default function SolarFieldHero({ className }: SolarFieldHeroProps) {
  return (
    <svg
      viewBox="0 0 480 120"
      preserveAspectRatio="xMidYMid slice"
      role="img"
      aria-hidden="true"
      className={`engraved-banner sorigin-banner ${className ?? ''}`}
    >
      <defs>
        <linearGradient id="sfh-tint" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FBFEF9" />
          <stop offset="100%" stopColor="#F2F8EC" />
        </linearGradient>

        <linearGradient id="sfh-pole" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#006346" stopOpacity="0.72" />
          <stop offset="100%" stopColor="#076333" stopOpacity="0.55" />
        </linearGradient>

        <linearGradient id="sfh-support" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#006346" stopOpacity="0.62" />
          <stop offset="100%" stopColor="#076333" stopOpacity="0.42" />
        </linearGradient>

        <pattern id="sfh-microline" x="0" y="0" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="6" stroke="#006346" strokeOpacity="0.05" strokeWidth="0.4" />
        </pattern>
      </defs>

      {/* Layer 1 — faint mint parchment tint */}
      <rect width="480" height="120" fill="url(#sfh-tint)" />

      {/* Layer 2 — diagonal micro-line hatch (very faint) */}
      <rect width="480" height="120" fill="url(#sfh-microline)" />

      {/* Layer 3 — banknote frame */}
      <rect x="5" y="5" width="470" height="110" rx="2" fill="none" stroke="#006346" strokeOpacity="0.22" strokeWidth="0.8" />
      <rect x="10" y="10" width="460" height="100" rx="1" fill="none" stroke="#52BF9D" strokeOpacity="0.32" strokeWidth="0.45" strokeDasharray="2 2.5" />

      {/* Layer 4 — corner flourishes */}
      <g fill="none" stroke="#006346" strokeOpacity="0.55" strokeWidth="0.9" strokeLinecap="round">
        <path d="M 5 18 Q 5 5 18 5" />
        <path d="M 462 5 Q 475 5 475 18" />
        <path d="M 475 102 Q 475 115 462 115" />
        <path d="M 18 115 Q 5 115 5 102" />
      </g>
      <g fill="#A6CE39" fillOpacity="0.85">
        <circle cx="11" cy="11" r="1.4" />
        <circle cx="469" cy="11" r="1.4" />
        <circle cx="469" cy="109" r="1.4" />
        <circle cx="11" cy="109" r="1.4" />
      </g>

      {/* Layer 5 — left rosette medallion (behind solar focal) */}
      <g transform="translate(60, 60)" fill="none" stroke="#006346" strokeWidth="0.5">
        <circle r="44" strokeOpacity="0.12" />
        <circle r="38" strokeOpacity="0.18" strokeDasharray="0.6 2" />
        <g strokeOpacity="0.10">
          <ellipse rx="34" ry="20" />
          <ellipse rx="34" ry="20" transform="rotate(30)" />
          <ellipse rx="34" ry="20" transform="rotate(60)" />
          <ellipse rx="34" ry="20" transform="rotate(90)" />
          <ellipse rx="34" ry="20" transform="rotate(120)" />
          <ellipse rx="34" ry="20" transform="rotate(150)" />
        </g>
        <circle r="26" strokeOpacity="0.16" />
        <circle r="14" strokeOpacity="0.10" />
      </g>

      {/* Layer 6 — engraved sun (above the panels, slightly left of center) */}
      <g transform="translate(190, 38)">
        {/* Rotating ray ring */}
        <g className="eb-sun-rays">
          <g stroke="#006346" strokeOpacity="0.55" strokeWidth="0.55" strokeLinecap="round">
            {Array.from({ length: 8 }).map((_, i) => {
              const angle = (i * 360) / 8;
              return (
                <line key={i} x1="0" y1="-11" x2="0" y2="-16" transform={`rotate(${angle})`} />
              );
            })}
          </g>
          {/* Secondary shorter rays offset */}
          <g stroke="#52BF9D" strokeOpacity="0.55" strokeWidth="0.4" strokeLinecap="round">
            {Array.from({ length: 8 }).map((_, i) => {
              const angle = (i * 360) / 8 + 22.5;
              return (
                <line key={`s-${i}`} x1="0" y1="-12" x2="0" y2="-14.5" transform={`rotate(${angle})`} />
              );
            })}
          </g>
        </g>
        {/* Engraved concentric discs */}
        <circle r="9" fill="none" stroke="#006346" strokeOpacity="0.4" strokeWidth="0.45" strokeDasharray="0.6 1.5" />
        <circle r="7" fill="#A6CE39" fillOpacity="0.85" className="eb-pulse" />
        <circle r="7" fill="none" stroke="#006346" strokeOpacity="0.55" strokeWidth="0.45" />
        <circle r="3.5" fill="none" stroke="#006346" strokeOpacity="0.4" strokeWidth="0.4" />
        <circle r="1.2" fill="#006346" fillOpacity="0.7" />
      </g>

      {/* Layer 7 — ground hatching beneath the panels */}
      <g stroke="#006346" strokeOpacity="0.35" strokeWidth="0.5" strokeLinecap="round">
        <line x1="120" y1="98" x2="320" y2="98" />
        <line x1="112" y1="102" x2="328" y2="102" strokeDasharray="1 2" />
        <line x1="130" y1="106" x2="312" y2="106" strokeDasharray="0.6 2" />
        <line x1="118" y1="110" x2="322" y2="110" strokeDasharray="0.5 2.5" />
      </g>

      {/* Layer 8 — engraved solar panel array (3 tilted panels, perspective rises L->R) */}
      <g>
        {/* Contact shadow under the array */}
        <ellipse cx="220" cy="98" rx="110" ry="2.2" fill="#006346" fillOpacity="0.18" />

        {/* Support legs — drawn first so panels sit over them */}
        <g stroke="url(#sfh-support)" strokeWidth="1.1" strokeLinecap="round">
          {/* Panel A legs */}
          <line x1="132" y1="98" x2="135" y2="84" />
          <line x1="168" y1="98" x2="166" y2="74" />
          {/* Panel B legs */}
          <line x1="200" y1="98" x2="204" y2="80" />
          <line x1="236" y1="98" x2="234" y2="68" />
          {/* Panel C legs */}
          <line x1="268" y1="98" x2="272" y2="74" />
          <line x1="304" y1="98" x2="302" y2="62" />
        </g>

        {/* Three engraved panels — each tilted slightly so left edge dips, right rises */}
        <EngravedPanel x={150} y={78} width={42} height={18} tilt={-12} cols={4} rows={2} />
        <EngravedPanel x={218} y={73} width={42} height={18} tilt={-12} cols={4} rows={2} />
        <EngravedPanel x={286} y={67} width={42} height={18} tilt={-12} cols={4} rows={2} />
      </g>

      {/* Layer 9 — engraved wind turbine (decorative, right side) */}
      <g className="sb-turbine">
        {/* contact shadow */}
        <ellipse cx="420" cy="103" rx="9" ry="1.1" fill="#006346" fillOpacity="0.22" />

        {/* ground hatching */}
        <g stroke="#006346" strokeOpacity="0.35" strokeWidth="0.5" strokeLinecap="round">
          <line x1="410" y1="106" x2="430" y2="106" />
          <line x1="406" y1="109" x2="434" y2="109" strokeDasharray="1 2" />
          <line x1="412" y1="112" x2="428" y2="112" strokeDasharray="0.6 2" />
        </g>

        {/* tapered pole */}
        <path d="M 418.6 102.5 L 419.6 58 L 420.4 58 L 421.4 102.5 Z" fill="url(#sfh-pole)" />

        {/* nacelle */}
        <path d="M 416.5 60 L 420 55 L 423.5 60 Z" fill="#006346" fillOpacity="0.6" />
        <rect x="417.8" y="56.5" width="4.4" height="3" rx="0.5" fill="#006346" fillOpacity="0.55" />

        {/* hub + 3 blades (rotation pivot) */}
        <g transform="translate(420, 57.5)">
          <g className="sb-turbine-blades">
            <path d="M 2.2 0 Q 9 -1.2 17 -0.5 Q 22 0 17 0.5 Q 9 1.2 2.2 0 Z" fill="#006346" fillOpacity="0.7" />
            <path d="M 2.2 0 Q 9 -1.2 17 -0.5 Q 22 0 17 0.5 Q 9 1.2 2.2 0 Z" fill="#006346" fillOpacity="0.7" transform="rotate(120)" />
            <path d="M 2.2 0 Q 9 -1.2 17 -0.5 Q 22 0 17 0.5 Q 9 1.2 2.2 0 Z" fill="#006346" fillOpacity="0.7" transform="rotate(240)" />
          </g>
          <circle r="2.4" fill="#A6CE39" />
          <circle r="2.4" fill="none" stroke="#006346" strokeOpacity="0.85" strokeWidth="0.5" />
          <circle r="0.85" fill="#006346" />
        </g>
      </g>

      {/* Layer 10 — tiny lime sparkles */}
      <g fill="#A6CE39" fillOpacity="0.65">
        <circle cx="118" cy="20" r="0.9" />
        <circle cx="262" cy="22" r="0.7" />
        <circle cx="356" cy="30" r="0.9" />
        <circle cx="98" cy="92" r="0.7" />
        <circle cx="372" cy="100" r="0.8" />
      </g>

      {/* Layer 11 — denomination-style serial corners ("MW" bars) */}
      <g fill="#006346" fillOpacity="0.4">
        {/* top-left: tiny "MW" bars */}
        <rect x="22" y="20" width="14" height="1" rx="0.5" />
        <rect x="22" y="22.5" width="9" height="0.8" rx="0.4" />
        <rect x="22" y="25" width="6" height="0.7" rx="0.35" />
      </g>
      <g fill="#006346" fillOpacity="0.4">
        {/* bottom-right: mirrored "MW" bars */}
        <rect x="444" y="98" width="14" height="1" rx="0.5" />
        <rect x="449" y="100.5" width="9" height="0.8" rx="0.4" />
        <rect x="452" y="103" width="6" height="0.7" rx="0.35" />
      </g>
    </svg>
  );
}
